import { useCallback, useEffect, useRef } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import type { PackageBuildStatus } from '../../services/collection-packages'
import { getPackageStatus } from '../../services/collection-packages'

const POLL_INTERVAL_MS = 3000
const TERMINAL_STATUSES: ReadonlySet<string> = new Set([
  'ready',
  'completed',
  'failed',
  'cancelled',
])

export interface UseStatusPollingResult {
  pollStatus: (caseId: string, requestId: string) => void
  stopPolling: () => void
}

export function useStatusPolling(
  setStatus: Dispatch<SetStateAction<PackageBuildStatus | null>>
): UseStatusPollingResult {
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(
    () => () => {
      if (pollingRef.current) clearInterval(pollingRef.current)
    },
    []
  )

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
  }, [])

  const pollStatus = useCallback(
    (caseId: string, requestId: string): void => {
      stopPolling()
      getPackageStatus(caseId, requestId)
        .then(setStatus)
        .catch(() => {
          /* ignore initial poll errors */
        })
      pollingRef.current = setInterval(() => {
        void (async () => {
          try {
            const latest = await getPackageStatus(caseId, requestId)
            setStatus(latest)
            if (TERMINAL_STATUSES.has(latest.status)) stopPolling()
          } catch {
            /* continue polling on transient errors */
          }
        })()
      }, POLL_INTERVAL_MS)
    },
    [stopPolling, setStatus]
  )

  return { pollStatus, stopPolling }
}
